/**
 * Data-Room Claim Query Hooks (Phase 116.7-04)
 *
 * React Query hooks over the `claims` table. Surfaces data-room-sourced
 * verifiable claims (claim_origin='data-room') in the investor UI, with the
 * denormalized `verification_status` mirror so list views need no join for
 * the status badge.
 *
 * For the "why" on verified/contradicted rows we batch-hydrate the linked
 * `verification_results` rows (source_url / source_summary) by id in a single
 * `.in()` query — never per-row (no N+1).
 *
 * RLS already scopes visibility (claims migration 116.7-02): startup owners see
 * their own claims; investors with granted data_room_access read-only.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from './client';
import { useAuth } from './auth';
import type { VerificationStatus } from '@/lib/scoring/score-display';

// ---------------------------------------------------------------------------
// Query Keys (namespaced — must not collide with scoreKeys)
// ---------------------------------------------------------------------------

export const claimKeys = {
  all: ['data-room-claims'] as const,
  list: (startupId?: string) =>
    startupId
      ? (['data-room-claims', startupId] as const)
      : (['data-room-claims'] as const),
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single data-room claim as surfaced in the investor UI. */
export interface DataRoomClaim {
  id: string;
  claimType: string;
  entity: string;
  assertion: string;
  sourceQuote: string | null;
  sourceDocumentId: string | null;
  verificationStatus: VerificationStatus;
  /** The "why" — populated on verified/contradicted rows when present. */
  sourceUrl: string | null;
  sourceSummary: string | null;
  createdAt: string;
}

interface ClaimDbRow {
  id: string;
  claim_type: string;
  entity: string;
  assertion: string;
  source_quote: string | null;
  source_document_id: string | null;
  verification_status: VerificationStatus;
  verification_result_id: string | null;
  created_at: string;
}

interface VerificationResultRow {
  id: string;
  source_url: string | null;
  source_summary: string | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetch the data-room claims for a startup, ordered by document then recency.
 * RLS limits visibility to the owner / granted investor.
 */
export function useDataRoomClaims(startupId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: claimKeys.list(startupId),
    queryFn: async (): Promise<DataRoomClaim[]> => {
      if (!isSupabaseConfigured() || !startupId) return [];

      const { data, error } = await supabase
        .from('claims' as never)
        .select(
          'id, claim_type, entity, assertion, source_quote, source_document_id, verification_status, verification_result_id, created_at',
        )
        .eq('startup_id', startupId)
        .eq('claim_origin', 'data-room')
        .order('source_document_id', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;

      const rows = (data ?? []) as unknown as ClaimDbRow[];
      if (rows.length === 0) return [];

      // Batch-hydrate the "why" (source_url / source_summary) for rows that
      // carry a verification_result_id — one .in() query, never per-row.
      const resultIds = Array.from(
        new Set(rows.map((r) => r.verification_result_id).filter((id): id is string => !!id)),
      );
      const whyMap = new Map<string, { sourceUrl: string | null; sourceSummary: string | null }>();
      if (resultIds.length > 0) {
        const { data: results } = await supabase
          .from('verification_results')
          .select('id, source_url, source_summary')
          .in('id', resultIds);
        for (const vr of (results ?? []) as unknown as VerificationResultRow[]) {
          whyMap.set(vr.id, { sourceUrl: vr.source_url, sourceSummary: vr.source_summary });
        }
      }

      return rows.map((r) => {
        const why = r.verification_result_id ? whyMap.get(r.verification_result_id) : undefined;
        return {
          id: r.id,
          claimType: r.claim_type,
          entity: r.entity,
          assertion: r.assertion,
          sourceQuote: r.source_quote,
          sourceDocumentId: r.source_document_id,
          verificationStatus: r.verification_status,
          sourceUrl: why?.sourceUrl ?? null,
          sourceSummary: why?.sourceSummary ?? null,
          createdAt: r.created_at,
        };
      });
    },
    enabled: !!user && !!startupId,
    staleTime: 5 * 60 * 1000,
  });
}
